import {
  ReactElement,
  KeyboardEvent as ReactKeyboardEvent,
  MouseEvent as ReactMouseEvent,
  forwardRef,
  useCallback,
} from 'react';

import { useCallbackRef, useId } from '~shared/hooks';
import { normalizeKey } from '~shared/utils/normalize-keycode';

import { CLOSE_MENU, GO_TO_ITEM, OPEN_MENU } from './actions';
import { useDisposables } from './hooks/useDisposables';
import { useMenuContext } from './MenuContext';
import { Focus } from './utils/getIndex';

interface Props {
  children: ({ isOpen, props }) => ReactElement;
  disabled?: boolean;
  onClick?: () => void;
}

export const MenuButton = forwardRef<HTMLButtonElement, Props>(
  function MenuButton({ children, onClick, ...props }: Props, ref) {
    const [state, dispatch] = useMenuContext('MenuButton');
    const buttonRef = useCallbackRef(state.buttonRef, ref);
    const id = `menu-button-${useId()}`;
    const d = useDisposables();

    const handleKeyDown = useCallback(
      (event: ReactKeyboardEvent<HTMLButtonElement>) => {
        const targetKey = normalizeKey(event);

        switch (targetKey) {
          case ' ':
          case 'Enter':
          case 'ArrowDown':
            event.preventDefault();
            event.stopPropagation();
            dispatch({ type: OPEN_MENU });
            d.nextFrame(() =>
              dispatch({ type: GO_TO_ITEM, focus: Focus.FIRST })
            );
            break;

          case 'ArrowUp':
            event.preventDefault();
            event.stopPropagation();
            dispatch({ type: OPEN_MENU });
            d.nextFrame(() =>
              dispatch({ type: GO_TO_ITEM, focus: Focus.LAST })
            );
            break;
        }
      },
      [dispatch, d]
    );

    const handleKeyUp = useCallback(
      (event: ReactKeyboardEvent<HTMLButtonElement>) => {
        switch (normalizeKey(event)) {
          /**
           * In Firefox, `preventDefault()` in handleKeyDown for the
           * Space key doesn't cancel the keyUp event, which triggers
           * a click.
           */
          case ' ':
            event.preventDefault();
            break;
        }
      },
      []
    );

    const handlePress = useCallback(
      (event: ReactMouseEvent) => {
        if (props.disabled) return;
        if (onClick) {
          onClick();
        }

        if (state.isOpen === true) {
          dispatch({ type: CLOSE_MENU });
          d.nextFrame(() =>
            state.buttonRef.current?.focus({ preventScroll: true })
          );
        } else {
          event.preventDefault();
          event.stopPropagation();
          dispatch({ type: OPEN_MENU });
        }
      },
      [props.disabled, onClick, state.isOpen, state.buttonRef, dispatch, d]
    );

    const buttonProps = {
      ref: buttonRef,
      id,
      type: 'button',
      'aria-haspopup': true,
      'aria-controls': state.listRef.current?.id,
      'aria-expanded': props.disabled ? undefined : state.isOpen,
      onKeyDown: handleKeyDown,
      onKeyUp: handleKeyUp,
      onClick: handlePress,
    };

    return children({
      isOpen: state.isOpen,
      props: { ...buttonProps },
    });
  }
);
